import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { TrialBanner } from '../../components/TrialBanner';
import { PWAInstallPrompt } from '../../components/PWAInstallPrompt';
import PlayerOnboarding from '../../components/PlayerOnboarding';
import {
  ONBOARDING_FLAG_KEY,
  PLAYER_ONBOARDING_REOPEN_EVENT,
} from '../../components/PlayerOnboarding/constants';
import { loadPlaylistsIndex } from '../../store/player_favorites/favoritesService';
import { useLexicon } from '../../hooks/useLexicon';

import homeIcon from '../../images/icons/home.svg';
import homeActiveIcon from '../../images/icons/home_active.svg';
import matchesIcon from '../../images/icons/tournaments.svg';
import matchesActiveIcon from '../../images/icons/tournaments_active.svg';
import eventsIcon from '../../images/icons/myPlaylists.svg';
import eventsActiveIcon from '../../images/icons/myPlaylists_active.svg';
import favoritesIcon from '../../images/icons/favoritesLinks.svg';
import favoritesActiveIcon from '../../images/icons/favoritesLinks_active.svg';
import profileIcon from '../../images/icons/myAccount.svg';
import profileActiveIcon from '../../images/icons/myAccount_active.svg';

// `lid` is the lexicon id; the nav label renders l(lid) so it translates.
const NAV_ITEMS = [
  { to: '/player/dashboard', lid: 1032, icon: homeIcon, iconActive: homeActiveIcon },
  { to: '/player/matches', lid: 7, icon: matchesIcon, iconActive: matchesActiveIcon },
  { to: '/player/events', lid: 9, icon: eventsIcon, iconActive: eventsActiveIcon },
  { to: '/player/favorites', lid: 1851, icon: favoritesIcon, iconActive: favoritesActiveIcon },
  { to: '/player/profile', lid: 1796, icon: profileIcon, iconActive: profileActiveIcon },
];

export const PlayerLayout = () => {
  const dispatch = useDispatch();
  const l = useLexicon();
  // Admin "view-as" (C1): when a platform admin is inspecting the PWA as a
  // player, surface a banner so it's obvious this isn't their own data, with
  // a quick way to switch players.
  const viewAsPlayerId = useSelector((state) => state.user.viewAsPlayerId);
  const [onboardingOpen, setOnboardingOpen] = useState(
    () => typeof window !== 'undefined' && !localStorage.getItem(ONBOARDING_FLAG_KEY),
  );

  // Bootstrap the playlists index once the player layout mounts so the
  // FavoriteToggle on event rows can render its state without flicker.
  useEffect(() => {
    dispatch(loadPlaylistsIndex());
  }, [dispatch]);

  useEffect(() => {
    const handler = () => setOnboardingOpen(true);
    window.addEventListener(PLAYER_ONBOARDING_REOPEN_EVENT, handler);
    return () => window.removeEventListener(PLAYER_ONBOARDING_REOPEN_EVENT, handler);
  }, []);

  const dismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_FLAG_KEY, '1');
    setOnboardingOpen(false);
  };

  return (
    <Shell>
      <Content>
        <PWAInstallPrompt />
        <TrialBanner />
        {viewAsPlayerId ? (
          <ViewAsBanner>
            <span>Viewing as player #{viewAsPlayerId}</span>
            <ViewAsChange to="/player/view-as">Change</ViewAsChange>
          </ViewAsBanner>
        ) : null}
        <Outlet />
      </Content>
      <PlayerOnboarding open={onboardingOpen} onDismiss={dismissOnboarding} />
      <BottomNav>
        {NAV_ITEMS.map((item) => (
          <NavItem key={item.to} to={item.to} end>
            {({ isActive }) => (
              <>
                <NavIcon src={isActive ? item.iconActive : item.icon} alt="" />
                <span>{l(item.lid)}</span>
              </>
            )}
          </NavItem>
        ))}
      </BottomNav>
    </Shell>
  );
};

export default PlayerLayout;

const Shell = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  max-width: 480px;
  margin: 0 auto;
  position: relative;
  /* Belt-and-braces: any child that misbehaves (long player names without
     break points, pre-formatted blocks, third-party widgets) should not
     push the page past the viewport. Keeps mobile pixel-perfect. */
  overflow-x: hidden;
  width: 100%;
`;

const Content = styled.main`
  flex: 1;
  padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px calc(env(safe-area-inset-bottom, 0px) + 88px);
`;

const ViewAsBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(255, 196, 0, 0.14);
  border: 1px solid rgba(255, 196, 0, 0.35);
  font-size: 13px;
  color: ${(props) => props.theme.white};
`;

const ViewAsChange = styled(NavLink)`
  color: inherit;
  font-weight: 700;
  text-decoration: underline;
  white-space: nowrap;
`;

const BottomNav = styled.nav`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  bottom: 0;
  width: 100%;
  max-width: 480px;
  display: flex;
  justify-content: space-around;
  background: ${(props) => props.theme.black};
  border-top: 1px solid ${(props) => props.theme.lines};
  padding: 8px 0 calc(env(safe-area-inset-bottom, 0px) + 8px);
  z-index: 10;
`;

const NavItem = styled(NavLink)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 4px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-decoration: none;
  color: ${(props) => props.theme.blueish};

  &.active {
    color: ${(props) => props.theme.green};
  }
`;

const NavIcon = styled.img`
  width: 22px;
  height: 22px;
  display: block;
`;
